/**
 * Google Maps utilities for distance calculation and geocoding
 * Adapted from fleet-manager apps/web for use in web-components
 */

/**
 * Calculate delivery fee using Google Maps Distance Matrix API
 */
export interface DeliveryFeeCalculation {
  fee: number;
  distance: number | null;
  baseFee: number;
  distanceBasedFee: number;
}

/**
 * Geocode an address to get coordinates
 */
export interface GeocodeResult {
  lat: number;
  lng: number;
  formattedAddress: string;
}

export async function geocodeAddress(
  address: string,
): Promise<GeocodeResult | null> {
  return new Promise((resolve) => {
    if (!window.google || !window.google.maps) {
      console.warn("Google Maps not loaded");
      resolve(null);
      return;
    }

    const geocoder = new google.maps.Geocoder();

    geocoder.geocode({ address }, (results, status) => {
      if (status === "OK" && results && results[0]) {
        const location = results[0].geometry.location;
        resolve({
          lat: location.lat(),
          lng: location.lng(),
          formattedAddress: results[0].formatted_address,
        });
      } else {
        console.warn("Geocoding failed:", status);
        resolve(null);
      }
    });
  });
}

export async function calculateDeliveryFee(
  origin: { lat: number; lng: number } | string,
  destination: { lat: number; lng: number },
  baseFee: number,
  feePerMile: number,
): Promise<DeliveryFeeCalculation> {
  return new Promise((resolve, reject) => {
    if (!window.google || !window.google.maps) {
      console.warn("Google Maps not loaded, using base fee only");
      resolve({
        fee: baseFee,
        distance: null,
        baseFee,
        distanceBasedFee: 0,
      });
      return;
    }

    const service = new google.maps.DistanceMatrixService();

    const originLocation =
      typeof origin === "string"
        ? origin
        : new google.maps.LatLng(origin.lat, origin.lng);

    const destinationLocation = new google.maps.LatLng(
      destination.lat,
      destination.lng,
    );

    service.getDistanceMatrix(
      {
        origins: [originLocation],
        destinations: [destinationLocation],
        travelMode: google.maps.TravelMode.DRIVING,
        unitSystem: google.maps.UnitSystem.IMPERIAL, // Miles
      },
      (response, status) => {
        if (status === "OK" && response) {
          const result = response.rows[0]?.elements[0];

          if (result && result.status === "OK" && result.distance) {
            // Distance is returned in meters, convert to miles
            const distanceInMiles = result.distance.value / 1609.34;

            // Calculate distance-based fee
            const distanceBasedFee = distanceInMiles * feePerMile;

            // Total fee is base fee + distance-based fee
            const totalFee = baseFee + distanceBasedFee;

            resolve({
              fee: totalFee,
              distance: distanceInMiles,
              baseFee,
              distanceBasedFee,
            });
          } else {
            console.warn(
              "Distance calculation failed, using base fee only:",
              result?.status,
            );
            resolve({
              fee: baseFee,
              distance: null,
              baseFee,
              distanceBasedFee: 0,
            });
          }
        } else {
          console.error("Distance Matrix request failed:", status);
          resolve({
            fee: baseFee,
            distance: null,
            baseFee,
            distanceBasedFee: 0,
          });
        }
      },
    );
  });
}
